import EuroIcon from "@mui/icons-material/Euro";
import { LoadingButton } from "@mui/lab";
import { Box, DialogTitle, TextField } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import {
	ProjectSaleableServiceGroup,
	useCreateSaleableServiceGroupMutation,
	useUpdateSaleableServiceGroupMutation,
} from "@repo/server-api";
import { useTranslation } from "next-i18next";
import { useEffect, useRef, useState } from "react";
import * as Yup from "yup";

import { validErrorState } from "@repo/legacy/components/helper/Index";

import NumberTextField from "../fields/NumberTextField";

export default function DialogServiceGroup(
	props: Readonly<{
		open: boolean;
		setOpen: (value: boolean) => void;
		refreshData: () => void;
		service: ProjectSaleableServiceGroup | null;
	}>,
) {
	const { t } = useTranslation();

	const [open, setOpen] = useState(false);
	const [service, setService] = useState<ProjectSaleableServiceGroup | null>(null);

	const [saveClicked, setSaveClicked] = useState<boolean>(false);

	const [errors, setErrors] = useState<Record<string, boolean | object | string>>({});

	const [updateServiceGroup] = useUpdateSaleableServiceGroupMutation({
		variables: {
			id: service?.id ?? "",
			name: service?.name ?? "",
			sellingPrice: service?.sellingPrice ?? 0,
		},
		onCompleted(data) {
			if (data.updateSaleableServiceGroup.saleableServiceGroup) {
				props.refreshData();
				handleClose();
			}
		},
	});

	const [createServiceGroup] = useCreateSaleableServiceGroupMutation({
		variables: {
			name: service?.name ?? "",
			sellingPrice: service?.sellingPrice ?? 0,
		},
		onCompleted(data) {
			if (data.createSaleableServiceGroup.saleableServiceGroup) {
				props.refreshData();
				handleClose();
			}
		},
	});

	useEffect(() => {
		setOpen(props.open);
	}, [props.open]);

	useEffect(() => {
		if (props.service) {
			setService({
				...props.service,
			});
		}
	}, [props.service]);

	const sellingPriceRef = useRef<{
		validate: () => Promise<Record<string, boolean | object | string>>;
	} | null>(null);

	const validationSchema = Yup.object().shape({
		name: Yup.string().required(t("validation.name.required")),
	});

	const validateFields = async () => {
		const localErrors: Record<string, boolean | object | string> = {};
		try {
			await validationSchema.validate(service, { abortEarly: false });
		} catch (err) {
			if (err instanceof Yup.ValidationError) {
				err.inner.forEach((error) => {
					if (error.path) {
						localErrors[error.path] = error.message;
					}
				});
			}
		}

		localErrors.sellingPrice = (await sellingPriceRef.current?.validate()) || {};

		setErrors({ ...localErrors });

		return validErrorState(localErrors);
	};

	const handleClose = () => {
		setOpen(false);
		setErrors({});
		setService(props.service);
		props.setOpen(false);
		setSaveClicked(false);
	};

	const handleSubmit = async () => {
		const isValid = await validateFields();
		if (isValid) {
			setSaveClicked(true);
			if (service?.id) {
				updateServiceGroup();
			} else {
				createServiceGroup();
			}
			return;
		}
	};

	return (
		<Dialog open={open} maxWidth="sm" fullWidth={true} data-testid="service-group-dialog">
			<DialogTitle>
				{t("service-group") + " " + (props.service?.name ? props.service.name : "")}
			</DialogTitle>
			<DialogContent className="flex-col gap-5">
				<Box className="fullCenter">
					<TextField
						onChange={(event) => {
							setService(service ? { ...service, name: event.target.value ?? "" } : null);
							setErrors({ ...errors, name: "" });
						}}
						error={errors.name ? true : false}
						helperText={errors.name ? (errors.name as string) : ""}
						className="w-100"
						value={service?.name ?? ""}
						label={t("name")}
						variant="outlined"
					/>
				</Box>
				<Box className="fullCenter">
					<NumberTextField
						isLoaded={true}
						ref={sellingPriceRef}
						label={t("selling-price")}
						value={service?.sellingPrice ?? 0}
						setValue={(value) => {
							setService(service ? { ...service, sellingPrice: value ?? 0 } : null);
						}}
						icon={<EuroIcon />}
						decimalPlaces={2}
						errors={errors.sellingPrice as Record<string, string>}
						setErrors={(value) => setErrors({ ...errors, sellingPrice: value })}
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" onClick={handleClose} autoFocus>
					{t("cancel")}
				</Button>
				<LoadingButton
					loading={saveClicked}
					variant="contained"
					onClick={handleSubmit}
					className={saveClicked ? "" : "blueButton"}>
					{t("save")}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
}
